import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ChecklistItem,
  ChecklistStatus,
  RequirementLevel,
  UpdateChecklistItemRequest,
  BatchUpdateChecklistRequest,
} from '../models';

const REQUIREMENT_MAP: Record<string, RequirementLevel> = {
  MandatoryDeliverable: 'Obligatoire',
  RequiredActivity: 'Activité requise',
  Recommended: 'Recommandé',
  AsNeeded: 'Selon besoin',
  NotRequired: 'Non requis',
};

const EXECUTION_STATUS_MAP: Record<ChecklistStatus, string> = {
  'Non commencé': 'NotStarted',
  'En cours': 'InProgress',
  Complété: 'Completed',
  'En problème': 'Blocked',
  'Non applicable': 'NotStarted',
};

@Injectable({ providedIn: 'root' })
export class ChecklistService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getByProjectId(projectId: string): Observable<ChecklistItem[]> {
    return this.http
      .get<any[]>(`${this.apiUrl}/projects/${projectId}/checklist`)
      .pipe(map(rows => rows.map(row => this.mapItem(row))));
  }

  updateItem(itemId: string, req: UpdateChecklistItemRequest): Observable<any> {
    return this.http.patch(`${this.apiUrl}/project-checklist-items/${itemId}`, req);
  }

  batchUpdate(projectId: string, req: BatchUpdateChecklistRequest): Observable<any[]> {
    return this.http.patch<any[]>(
      `${this.apiUrl}/projects/${projectId}/checklist/batch`,
      req
    );
  }

  // ── Mapping helpers ────────────────────────────────────────────────────────

  mapItem(row: any): ChecklistItem {
    return {
      id: row.id,
      phase: row.sectionNameSnapshot,
      kind: row.activityKindSnapshot,
      title: row.titleSnapshot,
      requirement: REQUIREMENT_MAP[row.requirementLevelSnapshot] ?? 'Selon besoin',
      isApplicable: row.applicability === 'Applicable',
      status: this.statusLabel(row.applicability, row.status),
      date: row.completedDate,
      problemDate: row.problemDate,
      problemReason: row.problemReason,
      problemReasonId: row.problemReasonId,
      comment: row.comment,
      helpText: row.helpText || undefined,
      referenceUrl: row.referenceUrlSnapshot || undefined,
    };
  }

  statusLabel(applicability: string, value: string): ChecklistStatus {
    const map: Record<string, ChecklistStatus> = {
      NotStarted: 'Non commencé',
      InProgress: 'En cours',
      Completed: 'Complété',
      Blocked: 'En problème',
    };
    const status = map[value] ?? 'Non commencé';
    // Un livrable réalisé demeure coché dans l'historique, même s'il n'est
    // plus applicable au cadre courant.
    return applicability === 'NotApplicable' && status !== 'Complété'
      ? 'Non applicable'
      : status;
  }

  executionStatus(value: ChecklistStatus): string {
    return EXECUTION_STATUS_MAP[value] ?? 'NotStarted';
  }

  buildUpdateRequest(
    item: ChecklistItem,
    status: ChecklistStatus,
    completedDate: string | null,
    problemDate: string | null,
    problemReason: string | null,
    comment: string | null,
    actor: string
  ): UpdateChecklistItemRequest {
    return {
      applicability:
        status === 'Non applicable' ? 'NotApplicable' : 'Applicable',
      status: this.executionStatus(status),
      completedDate,
      problemDate,
      problemReason,
      comment,
      notApplicableReason: null,
      problemReasonId: item.problemReasonId ?? null,
      actor,
    };
  }

  buildBatchRequest(
    itemIds: string[],
    status: ChecklistStatus,
    completedDate: string | null,
    problemDate: string | null,
    problemReason: string | null,
    comment: string | null,
    actor: string,
    problemReasonId: number | null = null
  ): BatchUpdateChecklistRequest {
    return {
      itemIds,
      problemReasonId,
      applicability:
        status === 'Non applicable' ? 'NotApplicable' : 'Applicable',
      status: this.executionStatus(status),
      completedDate,
      problemDate,
      problemReason,
      comment,
      actor,
    };
  }
}
